import Glob from "./Glob.mjs";

import appBootstrapSetup from "./appBootstrapSetup.mjs";
import Keycloak from "keycloak-js";

/**
 * @private
 * @type {Record<string, any>}
 */
const cache = {};

/**
 * @function
 * @private
 * @async
 * @return {Promise<Keycloak|false>}
 */
const getKeycloak = async () => {
	if ( ! cache.keycloakSetup) {
		await appBootstrapSetup();
		const data = {
			proto: window.location.protocol,
			hostname: window.location.hostname,
			port: window.location.port,
			localpath: Glob.prop.uri.localpath
		};
		try{
			cache.keycloakSetup = await appLib.api('login/keycloak-config-fe', data);
			cache.keycloakSetup.ui_locales="it-IT it en";
			//console.log(cache.keycloakSetup)
		}catch (e){
			console.error("error calling config/login/keycloak: ", e);
			Glob.gotoPage("CannotAuth");
			return false;
		}
	}
	if ( ! cache.keycloak ) cache.keycloak = new Keycloak(cache.keycloakSetup);
	if ( ! cache.keycloak ) return false;
	if ( ! cache.keycloak.didInitialize ) {
		const initData = {
			onLoad: "login-required",
			redirectUri: window.location.href.replace(/\?.*/,'')+'?backFromAuth=true',
			checkLoginIframe: false,
			enableLogging: true,
			pkceMethod: "S256",
			flow:"standard",
			ui_locales: cache.keycloakSetup.ui_locales,
		};
		await cache.keycloak.init(initData);
	}
	return cache.keycloak;
}

const incrementRunning = ()=>{
	let x = Glob.getStatus('apiRunning') || 0;
	if ( x < 0 ) x = 0;
	Glob.setStatus('apiRunning', x +1);
}

const decrementRunning = ()=>{
	let x = Glob.getStatus('apiRunning') || 1;
	if ( x < 1 ) x = 1;
	Glob.setStatus('apiRunning', x -1);
}

if ( ! window.document.body.classList.contains('objectMoveAware')) {
	window.document.addEventListener("mousemove", (e) => {
		if (!cache.dragging?.element) return;
		// Calcola la nuova posizione assoluta
		const newX = e.clientX - cache.dragging.offsetX;
		const newY = e.clientY - cache.dragging.offsetY;
		// Aggiorna le proprietà top e left
		cache.dragging.element.style.left = `${newX}px`;
		cache.dragging.element.style.top = `${newY}px`;
	});
	window.document.body.classList.add('objectMoveAware');
}


/**
 * @private
 * @param {string|Object} path
 * @param {Object} [data]
 * @param {Object} [xh] X-Headers
 * @return {Promise<Object>}
 */
const api = async ( path, data = {}, xh = {} ) => {
	const params = typeof data?.params === 'object' ? structuredClone(data.params) : {};
	if ( typeof data?.params === 'object' ) data = data?.data || {};
	if ( typeof path === 'object') {
		Object.assign(path,params);
		path = `${path.entity}/${path.operation}`;
	}
	if ( path.startsWith('/') ) path = path.slice(1);
	const ul = Glob.getStatus('userLogin');
	if ( ul?.session ) {
		params.session = ul.session;
		params.username = ul.username;
	}
	const ac = Glob.getStatus('appConfig');
	if ( ac ) {
		if ( ac['x-header'] ) xh[ac['x-header'].name] = ac['x-header'].value;
	}
	let out;
	incrementRunning();
	try {
		out = await Glob.api( path, { params: params, data: data }, xh );
		decrementRunning();
		return out;
	} catch( err ) {
		decrementRunning();
		if ((err?.message || '').startsWith('403')) {
			Glob.setStatus('userLogin');
		} else {
			throw err;
		}
	}
}

export const appLib = {

	/**
	 * Generic API
	 * @param {string|Object} path
	 * @param {Object} [data]
	 * @return {Promise<Object>}
	 */
	api: ( path, data ) => api(path, data ),

	/**
	 * Remove from file/directory names path-sensitive characters
	 * @param {string} id The string to be cleaned
	 * @return {string} The cleaned string
	 */
	cleanId:  id  => (id ?? '').replace(/[^a-zA-Z0-9_.-]/g,"_"),

	/**
	 * @return {string}
	 */
	defaultLanguage: () => 'it',

	/**
	 * @return {string}
	 */
	getUsername: ()=> Glob.getStatus('userLogin')?.username,

	admin: () => !! Glob.getStatus('userLogin')?.role?.admin,
	enabled: () => {
		const ul = Glob.getStatus('userLogin');
		return !! (ul?.role?.admin || ul?.role?.user);
	},
	/**
	 * Is this a small screen?
	 * @return {boolean}
	 */
	isSmall: ()=> (window.innerWidth < 800),

	logout: async ( gotoHomepage ) => {
		Glob.setStatus('savedLogin', undefined, true);
		const uri = window.location.href.replace(/#!.*/,gotoHomepage ? '' : 'logout.html' );
		if ( cache.keycloak ) await cache.keycloak.logout({logoutMethod:'POST', redirectUri: uri});
		// for(const key in window.localStorage){
		// 	if(key.startsWith("kc-callback-")) window.localStorage.removeItem(key);
		// }
		// if ( cache.keycloak ) await cache.keycloak.logout({logoutMethod:'POST'});
		// delete cache.keycloakSetup;
		// delete cache.keycloak;
		// Glob.setStatus('userLogin');
		// if ( gotoHomepage ) {
		// 	window.location.href = './';
		// } else {
		// 	window.location.href = './logout.html';
		// }
	},

	/**
	 * Utility that puts focus on the first input field of a modal, convenient to be invoked from onOpened.
	 * @param {string} [autoFocusSelector] if not empty, all fields with this selector will be focused one after the other before the first one.
	 * @return {void}
	 */
	modalAutoFocus : ( autoFocusSelector ) => {
		const dlog = document.querySelector('.modal-dialog .modal-body');
		if ( ! dlog ) return;
		const input = dlog.querySelector('input[type="text"]:not([disabled]):not([readonly]), input[type="password"]:not([disabled]):not([readonly]), textarea:not([disabled]):not([readonly])');
		if ( autoFocusSelector ) {
			dlog.querySelectorAll(autoFocusSelector).forEach( ti => { ti.focus(); });
		}
		if ( input ) input.focus();
	},

	/**
	 * @return {Promise<boolean>}
	 */
	verifySession: async () => {
		if ( Glob.getStatus('userLogin')?.session ) return true;
		const lastSavedLogin = Glob.getStatus('savedLogin');
		if ( lastSavedLogin?.username && lastSavedLogin?.session ) {
			const payload = {
				params: {
					username: lastSavedLogin.username,
					session: lastSavedLogin.session,
				},
				data: {}
			};
			const auth = await appLib.api('login/verify-session', payload);
			if ( auth.success ) {
				Glob.setStatus('userLogin', lastSavedLogin);
				return true;
			}
			Glob.setStatus('savedLogin', undefined, true);
		}
		if (cache.verifyingSession) return false;
		cache.verifyingSession = true;
		let kc;
		try {
			kc = await getKeycloak();
		} catch (e) {
			console.error('verifySession getKeycloak',e);
			Glob.gotoPage('CannotAuth');
			delete cache.verifyingSession;
			return false;
		}
		//console.log( kc );
		try {
			await kc.loadUserInfo();
			// await kc.loadUserProfile();
			// console.log(kc);
		} catch(e) {
			console.error('verifySession loadUserInfo error',e);
		}
		//console.log( cache.keycloak );
		const authenticated = cache.keycloak?.authenticated;
		if (! authenticated) {
			// go to auth failed pge
			delete cache.verifyingSession;
			return false;
		}
		//console.log(kc);
		const username = kc.userInfo.preferred_username;
		const sessOut = await appLib.api(
			'login/verify-token',
			{
				username: username,
				token: kc.refreshToken
			}
		);
		//console.log( sessOut );
		const isAuth = !! sessOut?.session;
		if ( isAuth ) {
			Glob.setStatus('userLogin', sessOut );
			Glob.setStatus('savedLogin', sessOut, true);
			await api('setup-postgres-environment');
			if ( window.location.href.includes('?backFromAuth=true')) {
				const cp = Glob.getCurrentPage();
				cp.name = cp.name.replace(/\?backFromAuth=true.*/,'');
				if ( cp.params ) Object.keys( cp.params ).forEach(k => {
					cp.params[k] = cp.params[k].replace(/\?backFromAuth=true.*/,'');
				});
				setTimeout( ()=> { Glob.gotoPage(cp, true) }, 100 );
			}
		} else {
			Glob.setStatus('loginError', sessOut?.error );
			console.log('verifySession noAuth',sessOut);
			setTimeout( ()=> { Glob.gotoPage('CannotAuth' ) }, 100 );
		}
		delete cache.verifyingSession;
		return isAuth;
	},
	strategicItems: async () => {
		if ( ! cache.strategicItems ) {
			cache.loadingItems = true;
			cache.strategicItems = (await api("simul/list-items-strategico"))?.list;
			cache.loadingItems = false;
		}
		return structuredClone(cache.strategicItems);
	},
	strategicOptions: async () => {
		if ( ! cache.strategicOptions ) {
			const list = await appLib.strategicItems();
			if ( list ) {
				const groups = {};
				list.forEach( item => {
					if ( ! groups[item.settore] ) groups[item.settore] = [];
					groups[item.settore].push({ label: item.name, value: item.id });
				});
				const out = [];
				const groupNames = Object.keys( groups ).sort();
				for ( const group of groupNames ) {
					groups[group].sort( (a,b) => a.label.localeCompare(b.label) );
					out.push({ label: group, options: groups[group] });
				}
				cache.strategicOptions = out;
			}
		}
		return structuredClone(cache.strategicOptions);
	},
	/**
	 * @function
	 * @param {Number|string} [d] The number of minutes (defaults to 0)
	 * @return {string} The string representation
	 */
	min2str: d => {
		if (d && typeof d === 'string') d = parseInt(d);
		if ( ! d ) return '—';
		if ( d <= 90 ) return `${d}’`;
		if ( d < (36 * 60 )) return `${parseInt( d / 60)}:${String(parseInt(d % 60 )).padStart(2,'0')}’`;
		return `${parseInt( d / 1440)}d ${parseInt( (d % 1440) / 60)}:${String(parseInt(d % 60 )).padStart(2,'0')}’`;
	},
	/**
	 * @function
	 * @param {string} id
	 * @return {{name: string, settore?: string}} Item name
	 */
	strategicItemName: id => {
		const s = cache.strategicItems;
		if ( ! s ) {
			appLib.strategicItems();
			return {name: id};
		}
		const found = s.find( item => item.id === id );
		return { name: found?.name ?? id, settore: found?.settore ?? false };
	},
	dragging: (element, offsetX, offsetY) => {
		if ( typeof element === 'undefined' ) return [cache.dragging?.element, cache.dragging.offsetX, cache.dragging.offsetY];
		if ( ! element ) {
			cache.dragging = {};
			return;
		}
		cache.dragging = { element: element, offsetX: offsetX, offsetY: offsetY };
	}
}

export default appLib;
